/**
 * projectService.js - Service per gestire progetti e materiali
 * CRUD completo su Supabase, ZERO mock data
 */

import supabase from '../lib/supabase';
import { getCachedUser } from '../hooks/useAuth';

// ============================================================================
// PROJECTS CRUD
// ============================================================================

/**
 * Get all projects for current user
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getUserProjects = async () => {
  try {
    const user = getCachedUser();
    if (!user) return { data: [], error: new Error('Not authenticated') };

    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_materials (
          id,
          material_id,
          material_name,
          material_image,
          material_type,
          notes,
          sort_order
        )
      `)
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (err) {
    console.error('getUserProjects error:', err);
    return { data: [], error: err };
  }
};

/**
 * Get single project by ID
 * @param {string} projectId - Project UUID
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const getProjectById = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_materials (
          id,
          material_id,
          material_name,
          material_image,
          material_type,
          notes,
          sort_order,
          materials:material_id (
            id,
            name,
            image_url,
            category,
            dominant_color,
            pattern,
            description
          )
        )
      `)
      .eq('id', projectId)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    console.error('getProjectById error:', err);
    return { data: null, error: err };
  }
};

/**
 * Create new project
 * @param {Object} projectData - { name, description, image, client, cover_image }
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const createProject = async (projectData) => {
  try {
    const user = getCachedUser();
    if (!user) return { data: null, error: new Error('Not authenticated') };

    const { data, error } = await supabase
      .from('projects')
      .insert({
        user_id: user.id,
        name: projectData.name,
        description: projectData.description || null,
        image: projectData.image || null,
        cover_image: projectData.cover_image || null,
        client: projectData.client || null,
        is_demo: false,
        status: 'active'
      })
      .select()
      .single();

    if (error) throw error;
    console.log('Project created:', data.id);
    return { data, error: null };
  } catch (err) {
    console.error('createProject error:', err);
    return { data: null, error: err };
  }
};

/**
 * Update project
 * @param {string} projectId - Project UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const updateProject = async (projectId, updates) => {
  try {
    // If updating, convert demo to real
    const cleanUpdates = { ...updates };
    delete cleanUpdates.id;
    delete cleanUpdates.user_id;
    delete cleanUpdates.created_at;
    
    // Convert demo to real on any update
    cleanUpdates.is_demo = false;
    cleanUpdates.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('projects')
      .update(cleanUpdates)
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    console.log('Project updated:', projectId);
    return { data, error: null };
  } catch (err) {
    console.error('updateProject error:', err);
    return { data: null, error: err };
  }
};

/**
 * Delete project
 * @param {string} projectId - Project UUID
 * @returns {Promise<{error: Error|null}>}
 */
export const deleteProject = async (projectId) => {
  try {
    // project_materials will cascade delete
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;
    console.log('Project deleted:', projectId);
    return { error: null };
  } catch (err) {
    console.error('deleteProject error:', err);
    return { error: err };
  }
};

// ============================================================================
// PROJECT MATERIALS
// ============================================================================

/**
 * Get materials for a project
 * @param {string} projectId - Project UUID
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getProjectMaterials = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('project_materials')
      .select(`
        *,
        materials:material_id (
          id,
          name,
          image_url,
          category,
          dominant_color,
          pattern,
          description
        )
      `)
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (err) {
    console.error('getProjectMaterials error:', err);
    return { data: [], error: err };
  }
};

/**
 * Add material to project
 * @param {string} projectId - Project UUID
 * @param {Object} materialData - { material_id, material_name, material_image, material_type, notes }
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const addMaterialToProject = async (projectId, materialData) => {
  try {
    // Check if already exists
    if (materialData.material_id) {
      const { data: existing } = await supabase
        .from('project_materials')
        .select('id')
        .eq('project_id', projectId)
        .eq('material_id', materialData.material_id)
        .single();

      if (existing) {
        return { data: existing, error: new Error('Material already in project') };
      }
    }

    // Get current max sort_order
    const { data: maxOrder } = await supabase
      .from('project_materials')
      .select('sort_order')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: false })
      .limit(1)
      .single();

    const nextOrder = (maxOrder?.sort_order || 0) + 1;

    const { data, error } = await supabase
      .from('project_materials')
      .insert({
        project_id: projectId,
        material_id: materialData.material_id || null,
        material_name: materialData.material_name || materialData.name,
        material_image: materialData.material_image || materialData.image_url,
        material_type: materialData.material_type || materialData.category,
        notes: materialData.notes || null,
        sort_order: nextOrder
      })
      .select()
      .single();

    if (error) throw error;

    // Update project timestamp (and convert demo to real)
    await supabase
      .from('projects')
      .update({ 
        updated_at: new Date().toISOString(),
        is_demo: false
      })
      .eq('id', projectId);

    console.log('Material added to project:', data.id);
    return { data, error: null };
  } catch (err) {
    console.error('addMaterialToProject error:', err);
    return { data: null, error: err };
  }
};

/**
 * Remove material from project
 * @param {string} projectMaterialId - project_materials row UUID
 * @returns {Promise<{error: Error|null}>}
 */
export const removeMaterialFromProject = async (projectMaterialId) => {
  try {
    const { error } = await supabase
      .from('project_materials')
      .delete()
      .eq('id', projectMaterialId);

    if (error) throw error;
    console.log('Material removed from project:', projectMaterialId);
    return { error: null };
  } catch (err) {
    console.error('removeMaterialFromProject error:', err);
    return { error: err };
  }
};

/**
 * Update material notes in project
 * @param {string} projectMaterialId - project_materials row UUID
 * @param {string} notes - New notes
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const updateProjectMaterialNotes = async (projectMaterialId, notes) => {
  try {
    const { data, error } = await supabase
      .from('project_materials')
      .update({ notes })
      .eq('id', projectMaterialId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    console.error('updateProjectMaterialNotes error:', err);
    return { data: null, error: err };
  }
};

/**
 * Reorder materials in project
 * @param {string} projectId - Project UUID
 * @param {Array} orderedIds - Array of project_materials IDs in new order
 * @returns {Promise<{error: Error|null}>}
 */
export const reorderProjectMaterials = async (projectId, orderedIds) => {
  try {
    const updates = orderedIds.map((id, index) => 
      supabase
        .from('project_materials')
        .update({ sort_order: index })
        .eq('id', id)
    );

    await Promise.all(updates);
    console.log('Materials reordered for project:', projectId);
    return { error: null };
  } catch (err) {
    console.error('reorderProjectMaterials error:', err);
    return { error: err };
  }
};

// ============================================================================
// MATERIALS (Read-only for users)
// ============================================================================

/**
 * Get all materials
 * @param {Object} filters - { category, dominant_color, pattern, search, limit }
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getMaterials = async (filters = {}) => {
  try {
    let query = supabase
      .from('materials')
      .select('*');

    if (filters.category) {
      query = query.eq('category', filters.category);
    }
    if (filters.dominant_color) {
      query = query.eq('dominant_color', filters.dominant_color);
    }
    if (filters.pattern) {
      query = query.eq('pattern', filters.pattern);
    }
    if (filters.search) {
      query = query.ilike('name', `%${filters.search}%`);
    }
    if (filters.is_featured) {
      query = query.eq('is_featured', true);
    }
    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    query = query.order('name', { ascending: true });

    const { data, error } = await query;

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (err) {
    console.error('getMaterials error:', err);
    return { data: [], error: err };
  }
};

/**
 * Get single material by ID
 * @param {string} materialId - Material UUID
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const getMaterialById = async (materialId) => {
  try {
    const { data, error } = await supabase
      .from('materials')
      .select('*')
      .eq('id', materialId)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    console.error('getMaterialById error:', err);
    return { data: null, error: err };
  }
};

/**
 * Get featured materials
 * @param {number} limit - Max number of materials
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getFeaturedMaterials = async (limit = 8) => {
  return getMaterials({ is_featured: true, limit });
};

/**
 * Get unique categories
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('materials')
      .select('category')
      .not('category', 'is', null);

    if (error) throw error;

    const unique = [...new Set(data.map(m => m.category).filter(Boolean))];
    return { data: unique, error: null };
  } catch (err) {
    console.error('getCategories error:', err);
    return { data: [], error: err };
  }
};

/**
 * Get unique colors
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getColors = async () => {
  try {
    const { data, error } = await supabase
      .from('materials')
      .select('dominant_color')
      .not('dominant_color', 'is', null);

    if (error) throw error;

    const unique = [...new Set(data.map(m => m.dominant_color).filter(Boolean))];
    return { data: unique, error: null };
  } catch (err) {
    console.error('getColors error:', err);
    return { data: [], error: err };
  }
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  // Projects
  getUserProjects,
  getProjectById,
  createProject,
  updateProject,
  deleteProject,
  
  // Project Materials
  getProjectMaterials,
  addMaterialToProject,
  removeMaterialFromProject,
  updateProjectMaterialNotes,
  reorderProjectMaterials,
  
  // Materials
  getMaterials,
  getMaterialById,
  getFeaturedMaterials,
  getCategories,
  getColors
};
